import {
  Box,
  Button,
  CircularProgress,
  DialogContent,
  Popover,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import moment from "moment";
import { QRCodeSVG } from "qrcode.react";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { AiOutlineCloudDownload, AiOutlineMail } from "react-icons/ai";
import { BiBarcodeReader } from "react-icons/bi";
import { ImCopy } from "react-icons/im";
import { IoCloseOutline } from "react-icons/io5";
import { SiPix } from "react-icons/si";
import { toast } from "react-toastify";
import { Parcel } from "src/pages/dashboard/HistoricPayments";
import axiosInstance from "src/utils/axios";

interface IGetBolecode {
  RETORNOS: [
    {
      CODBAR: string;
      LDIGIT: string;
      VALOR: 76.45;
      VENCIMENTO: string;
      BOLETO_LINK: string;
      BOLETO_PIX: string;
    }
  ];
}

// ----------------------------- COMPONENTE PRINCIPAL -------------------------------------
export interface FormDialogProps {
  open: boolean;
  onClose: () => void;
  parcel: Parcel;
  getHistoric: () => Promise<void>;
  setReloadNextPayment: Dispatch<SetStateAction<boolean>>;
}

export function ModalPayments(props: FormDialogProps) {
  // Constantes
  const theme = useTheme();
  const { onClose, open, getHistoric, setReloadNextPayment } = props;

  // --- hooks
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [tab, setTab] = useState<"pix" | "boleto">("pix");
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [paym, setPaym] = useState<Parcel>(props.parcel);

  const openPopover = Boolean(anchorEl);
  const id = openPopover ? "simple-popover" : undefined;

  const showTabs =
    paym.copy_pix.length > 0 && moment(new Date()).diff(moment(paym.due_date), "days") <= 45;
  const currentTab = showTabs ? tab : "boleto";

  // ----------------------------- FUNÇÕES -------------------------------------
  const handleClickPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleClose = async (reload: boolean) => {
    onClose();
    setTab("pix");

    if (reload) {
      setReloadNextPayment(true);
      await getHistoric();
    }
  };

  const downloadBoleto = () => {
    if (paym.ticket_url) {
      const a = Object.assign(document.createElement("a"), {
        href: paym.ticket_url,
        style: "display:none",
        download: "boleto.pdf",
      });
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  };

  const handleGetBolecode = async () => {
    setLoadingPayment(true);
    await axiosInstance
      .post("/users/getBolecode", {
        company: props.parcel.company,
        filial: props.parcel.filial,
        num: props.parcel.num,
        prefix: props.parcel.prefix,
        installment: props.parcel.installment,
        type: props.parcel.type,
        bussines: props.parcel.bussines,
      })
      .then(async (response) => {
        const { RETORNOS }: IGetBolecode = response.data;
        if (RETORNOS[0]) {
          const data = RETORNOS[0];
          setPaym({
            ...paym,
            due_date: data.VENCIMENTO,
            total: String(data.VALOR),
            barcode: data.CODBAR,
            barcode_line: data.LDIGIT,
            copy_pix: RETORNOS[0].BOLETO_PIX.trim(),
            ticket_url: RETORNOS[0].BOLETO_LINK,
          });
          toast.success(`Dados do pagamento atualizados com sucesso!`, {
            position: toast.POSITION.TOP_RIGHT,
            theme: "colored",
          });
        } else {
          await handleClose(false);
          toast.info(`O pagamento não foi necessário ser atualizado`, {
            position: toast.POSITION.TOP_RIGHT,
            theme: "colored",
          });
        }
      })
      .catch(async (error) => {
        setLoadingPayment(false);
        await handleClose(false);
        toast.error(`Ocorreu um erro ao buscar o pagamento: ${error.message}`, {
          position: toast.POSITION.TOP_RIGHT,
          theme: "colored",
        });
      });
    setLoadingPayment(false);
  };

  useEffect(() => {
    handleGetBolecode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.parcel]);

  const handleSendEmail = async () => {
    setSendingEmail(true);
    await axiosInstance
      .post("/users/send-boleto", {
        boleto_url: paym.ticket_url,
        code_pix: currentTab === "pix" ? paym.copy_pix : undefined,
        ldigit: paym.barcode_line,
        valor: paym.total,
        vencimento: paym.due_date,
      })
      .then(() => {
        toast.success(`Enviamos o boleto para o seu e-mail cadastrado!`, {
          position: toast.POSITION.TOP_RIGHT,
          theme: "colored",
        });
      })
      .catch((error) => {
        toast.error(
          error?.response?.data?.message || `Ocorreu um erro ao enviar o e-mail: ${error.message}`,
          {
            position: toast.POSITION.TOP_RIGHT,
            theme: "colored",
          }
        );
      });
    setSendingEmail(false);
  };

  interface IPaymentData {
    due_date_corR: string;
    date: string;
    value: string;
  }

  const PaymentData = ({ date, value, due_date_corR }: IPaymentData) => (
    //  ------------------- Valor e data da parcela -------------------
    <Stack
      textAlign="center"
      direction={{ xs: "column", sm: "row" }}
      spacing={{ xs: 1, sm: 4, md: 2 }}
      justifyContent="space-around"
    >
      <Box>
        <Typography
          variant="body2"
          fontWeight="400"
          color={theme.palette.grey[800]}
          fontSize="14px"
        >
          Mês da parcela
        </Typography>
        <Typography
          variant="body2"
          fontWeight="700"
          color={theme.palette.grey[800]}
          fontSize="14px"
        >
          {moment(due_date_corR).format("MMM/YYYY").toUpperCase()}
        </Typography>
      </Box>
      <Box>
        <Typography
          variant="body2"
          fontWeight="400"
          color={theme.palette.grey[800]}
          fontSize="14px"
        >
          Vencimento
        </Typography>
        <Typography
          variant="body2"
          fontWeight="700"
          color={theme.palette.grey[800]}
          fontSize="14px"
        >
          {moment(date).format("DD/MM/YYYY")}
        </Typography>
      </Box>
      <Box>
        <Typography
          variant="body2"
          fontWeight="400"
          color={theme.palette.grey[800]}
          fontSize="14px"
        >
          Valor
        </Typography>
        <Typography
          variant="body2"
          fontWeight="700"
          color={theme.palette.secondary.main}
          fontSize="14px"
        >
          {new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
          }).format(Number(value))}
        </Typography>
      </Box>
    </Stack>
  );

  // ----------------------------- JSX -------------------------------------
  return (
    <Dialog onClose={async () => handleClose(true)} open={open} fullWidth maxWidth="sm">
      <DialogContent sx={{ padding: "0px" }}>
        <div
          style={{
            padding: "20px 20px 24px 20px",
            borderBottom: "1px solid  rgba(145, 158, 171, 0.24)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
          >
            Pagar Fatura
          </Typography>

          <IoCloseOutline
            size={24}
            onClick={async () => handleClose(true)}
            style={{ cursor: "pointer" }}
          />
        </div>

        {/*  ------------------- Resumo da parcela ------------------- */}
        <Box sx={{ backgroundColor: theme.palette.background.neutral, padding: "16px 20px" }}>
          <PaymentData date={paym.due_date} value={paym.total} due_date_corR={paym.due_date_corR} />
        </Box>

        {/*  ------------------- Abas PIX / Boleto ------------------- */}
        {showTabs && (
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            variant="fullWidth"
            indicatorColor="secondary"
            textColor="secondary"
            sx={{ borderBottom: "1px solid rgba(145, 158, 171, 0.24)" }}
          >
            <Tab
              value="pix"
              label="PIX"
              icon={<SiPix style={{ height: 20, width: 20 }} />}
              iconPosition="start"
              sx={{ "&.Mui-selected": { color: theme.palette.secondary.main } }}
            />
            <Tab
              value="boleto"
              label="Boleto"
              icon={<BiBarcodeReader style={{ height: 20, width: 20 }} />}
              iconPosition="start"
              sx={{ "&.Mui-selected": { color: theme.palette.secondary.main } }}
            />
          </Tabs>
        )}

        {/*  ------------------- Conteúdo da aba ------------------- */}
        <Box
          sx={{
            padding: "24px 20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {currentTab === "pix" ? (
            <>
              <Typography
                variant="h6"
                color={theme.palette.secondary.main}
                textAlign="center"
                gutterBottom
              >
                Pagamento via PIX
              </Typography>
              <Typography variant="body2" textAlign="center">
                Copie ou escaneie o QR Code.
              </Typography>
              <Typography
                variant="body2"
                color={theme.palette.grey[600]}
                textAlign="center"
                sx={{ mb: 3 }}
              >
                Ao copiar o código, abra o aplicativo do seu banco e realize o pagamento de forma
                rápida.
              </Typography>

              {!loadingPayment ? (
                <>
                  <QRCodeSVG
                    value={paym.copy_pix.trim()}
                    size={200}
                    bgColor={"#ffffff"}
                    fgColor={theme.palette.grey[900]}
                    level={"L"}
                    includeMargin={false}
                  />
                  <Box
                    sx={{
                      backgroundColor: theme.palette.grey[100],
                      borderRadius: 1,
                      padding: 2,
                      marginTop: 3,
                      width: "100%",
                    }}
                  >
                    <Typography
                      variant="body2"
                      color={theme.palette.grey[800]}
                      textAlign="center"
                      sx={{ wordBreak: "break-all" }}
                    >
                      {paym.copy_pix.trim()}
                    </Typography>
                  </Box>

                  <Stack
                    spacing={1.5}
                    sx={{ width: "100%", marginTop: 3, "& .MuiButton-root": { py: 1.5 } }}
                  >
                    <CopyToClipboard text={paym.copy_pix.trim()}>
                      <Button
                        aria-describedby={id}
                        onClick={handleClickPopover}
                        variant="contained"
                        color="secondary"
                        fullWidth
                        startIcon={<ImCopy style={{ width: 18, height: 18 }} />}
                      >
                        Copiar código
                      </Button>
                    </CopyToClipboard>
                    <Button
                      onClick={handleSendEmail}
                      variant="outlined"
                      color="secondary"
                      fullWidth
                      disabled={sendingEmail}
                      startIcon={
                        sendingEmail ? (
                          <CircularProgress size={16} />
                        ) : (
                          <AiOutlineMail style={{ width: 18, height: 18 }} />
                        )
                      }
                    >
                      Enviar por e-mail
                    </Button>
                  </Stack>

                  <Popover
                    id={id}
                    open={openPopover}
                    anchorEl={anchorEl}
                    onClose={handleClosePopover}
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    style={{ marginLeft: 5 }}
                  >
                    <Typography sx={{ p: 2 }}>Código copiado!</Typography>
                  </Popover>
                </>
              ) : (
                <>
                  <Typography
                    variant="body1"
                    fontSize={"16px"}
                    fontWeight="400"
                    color="text.primary"
                    textAlign="center"
                    mt={5}
                  >
                    {`Estamos processando o pix, por favor, aguarde um instante.`}
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "center", margin: "20px" }}>
                    <CircularProgress size={"15px"} />
                  </Box>
                </>
              )}
            </>
          ) : (
            <>
              <Typography
                variant="h6"
                color={theme.palette.secondary.main}
                textAlign="center"
                gutterBottom
              >
                Pagamento via boleto
              </Typography>
              <Typography
                variant="body2"
                color={theme.palette.grey[600]}
                textAlign="center"
                sx={{ mb: 3 }}
              >
                Copie a linha digitável abaixo para pagar no app do seu banco.
              </Typography>

              {!loadingPayment ? (
                <>
                  {paym.barcode_line !== "" && (
                    <Box
                      sx={{
                        backgroundColor: theme.palette.grey[100],
                        borderRadius: 1,
                        padding: 2,
                        width: "100%",
                      }}
                    >
                      <Typography
                        variant="body2"
                        color={theme.palette.grey[800]}
                        textAlign="center"
                        sx={{ wordBreak: "break-all" }}
                      >
                        {paym.barcode_line}
                      </Typography>
                    </Box>
                  )}

                  <Stack
                    spacing={1.5}
                    sx={{ width: "100%", marginTop: 3, "& .MuiButton-root": { py: 1.4 } }}
                  >
                    <CopyToClipboard text={paym.barcode_line}>
                      <Button
                        aria-describedby={id}
                        onClick={handleClickPopover}
                        variant="contained"
                        color="secondary"
                        fullWidth
                        startIcon={<ImCopy style={{ width: 18, height: 18 }} />}
                      >
                        Copiar código
                      </Button>
                    </CopyToClipboard>
                    <Button
                      onClick={() => downloadBoleto()}
                      variant="outlined"
                      color="secondary"
                      fullWidth
                      startIcon={<AiOutlineCloudDownload style={{ width: 18, height: 18 }} />}
                    >
                      Visualizar boleto
                    </Button>
                    <Button
                      onClick={handleSendEmail}
                      variant="outlined"
                      color="secondary"
                      fullWidth
                      disabled={sendingEmail}
                      startIcon={
                        sendingEmail ? (
                          <CircularProgress size={16} />
                        ) : (
                          <AiOutlineMail style={{ width: 18, height: 18 }} />
                        )
                      }
                    >
                      Enviar por e-mail
                    </Button>
                  </Stack>

                  <Popover
                    id={id}
                    open={openPopover}
                    anchorEl={anchorEl}
                    onClose={handleClosePopover}
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    style={{ marginLeft: 5 }}
                  >
                    <Typography sx={{ p: 2 }}>Código copiado!</Typography>
                  </Popover>
                </>
              ) : (
                <>
                  <Typography
                    variant="body1"
                    fontSize={"16px"}
                    fontWeight="400"
                    color="text.primary"
                    textAlign="center"
                    mt={5}
                  >
                    {`Estamos processando o boleto, por favor, aguarde um instante.`}
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "center", margin: "20px" }}>
                    <CircularProgress size={"15px"} />
                  </Box>
                </>
              )}
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

